import React, { useState } from "react";

// ── Copy Button ─────────────────────────────────────────────
export function CopyButton({ text, label = "Copy", className = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      className={`mcp-copy-btn ${className}`}
      onClick={handleCopy}
    >
      {copied ? "Copied!" : label}
    </button>
  );
}

// ── Toggle Switch ───────────────────────────────────────────
export function Toggle({ checked, onChange, disabled = false, label, sensitive = false }) {
  return (
    <label className={`mcp-toggle-wrapper ${disabled ? "mcp-disabled" : ""}`}>
      <span className="mcp-toggle-label">
        {label}
        {sensitive && (
          <span className={`mcp-badge ${checked ? "mcp-badge-amber" : "mcp-badge-gray"}`}>
            Sensitive
          </span>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={`mcp-toggle ${checked ? "mcp-toggle-on" : "mcp-toggle-off"}`}
        onClick={() => onChange(!checked)}
      >
        <span className="mcp-toggle-knob" />
      </button>
    </label>
  );
}

// ── Section Card ────────────────────────────────────────────
export function Section({ title, subtitle, children, id }) {
  return (
    <div className="mcp-section" id={id}>
      <div className="mcp-section-header">
        <h3 className="mcp-section-title">{title}</h3>
        {subtitle && <p className="mcp-section-subtitle">{subtitle}</p>}
      </div>
      <div className="mcp-section-body">{children}</div>
    </div>
  );
}

// ── Number Input ────────────────────────────────────────────
export function NumberInput({ label, value, onChange, disabled = false, prefix = "$", min = 0, placeholder }) {
  return (
    <div className="mcp-number-input-group">
      <label className="mcp-number-label">{label}</label>
      <div className="mcp-number-input-wrapper">
        {prefix && <span className="mcp-number-prefix">{prefix}</span>}
        <input
          type="number"
          className="mcp-number-input"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          min={min}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────
export function Badge({ variant = "gray", children }) {
  return <span className={`mcp-badge mcp-badge-${variant}`}>{children}</span>;
}

// ── Loading Skeleton ────────────────────────────────────────
export function Skeleton({ lines = 3 }) {
  return (
    <div className="mcp-skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="mcp-skeleton-line"
          style={{ width: `${70 + Math.random() * 30}%` }}
        />
      ))}
    </div>
  );
}

// ── Empty State ─────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="mcp-empty-state">
      {icon && <div className="mcp-empty-icon">{icon}</div>}
      <h4 className="mcp-empty-title">{title}</h4>
      {description && <p className="mcp-empty-desc">{description}</p>}
      {action}
    </div>
  );
}

// ── Truncate address helper ─────────────────────────────────
export function truncateAddress(addr, start = 6, end = 4) {
  if (!addr) return "";
  if (addr.length <= start + end) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
}
